import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import moment from 'moment-timezone';
import { ScheduleCalendarDayContentDirective } from '../../../directives/schedule-calendar-day-content.directive';
import { ClockService } from '../../../services/clock.service';
import { DATE_INPUT_FORMAT } from '../../../utils/input-formats';
import {
  CALENDAR_HEIGHT,
  CALENDAR_HOURS,
  CALENDAR_PIXELS_PER_MINUTE,
  DEFAULT_VISIBLE_MINUTE_RANGE,
  formatMinutes,
  getHiddenMinutes,
  ICalendarDay,
  ICalendarMinuteRange,
  minutesToPixels,
} from '../../../utils/schedule-calendar.utils';
import { toDayMinutes } from '../../../utils/schedule-timezone.utils';
import { ScheduleCalendarDayComponent } from '../schedule-calendar-day/schedule-calendar-day.component';

/**
 * Height of the sticky day headers above the scrolling grid. Passed to the
 * styles as --day-header-height.
 */
const DAY_HEADER_HEIGHT = 52;

/** The scroller's top and bottom border */
const SCROLLER_BORDERS = 2;

/** Height of the grid in view below the day headers, before any resizing */
const DEFAULT_VIEWPORT_HEIGHT = minutesToPixels(
  DEFAULT_VISIBLE_MINUTE_RANGE.end - DEFAULT_VISIBLE_MINUTE_RANGE.start
);

/** Space left above / below times scrolled to from a hint */
const SCROLL_HINT_MARGIN = minutesToPixels(30);

/**
 * A scrolling grid of the hours of the given days. What's shown on each day
 * is given by an `appScheduleCalendarDayContent` template.
 */
@Component({
  selector: 'app-schedule-calendar',
  templateUrl: './schedule-calendar.component.html',
  styleUrls: ['./schedule-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--day-header-height.px]': 'dayHeaderHeight',
    '[style.min-height.px]': 'minScrollerHeight',
  },
  imports: [ScheduleCalendarDayComponent, MatIconModule],
})
export class ScheduleCalendarComponent {
  /** YYYY-MM-DD dates in the timezone */
  readonly dates = input.required<string[]>();
  readonly timezoneName = input.required<string>();
  /** What's shown on the days, so hints can point to any scrolled out of view */
  readonly items = input<ICalendarMinuteRange[]>([]);

  readonly dayContent = contentChild(ScheduleCalendarDayContentDirective);

  private readonly scroller =
    viewChild.required<ElementRef<HTMLElement>>('scroller');

  private readonly destroyRef = inject(DestroyRef);
  private readonly clock = inject(ClockService);

  private readonly zonedNow = computed(() =>
    moment(this.clock.now()).tz(this.timezoneName())
  );

  /** On the wall clock, as the blocks are, rather than elapsed since midnight */
  readonly nowMinutes = computed(() => toDayMinutes(this.zonedNow()));

  readonly dayHeaderHeight = DAY_HEADER_HEIGHT;

  /** Fits the sticky day headers and the default visible range */
  readonly scrollerHeight =
    DAY_HEADER_HEIGHT + DEFAULT_VIEWPORT_HEIGHT + SCROLLER_BORDERS;

  /** The least the grid shrinks to in a short space: the headers and 4 hours */
  readonly minScrollerHeight =
    DAY_HEADER_HEIGHT + minutesToPixels(4 * 60) + SCROLLER_BORDERS;

  /** Height of the grid below the day headers that's in view */
  private readonly viewportHeight = signal(DEFAULT_VIEWPORT_HEIGHT);

  private readonly scrollTop = signal(
    minutesToPixels(DEFAULT_VISIBLE_MINUTE_RANGE.start)
  );

  private readonly visibleMinuteRange = computed(() => {
    const start = this.scrollTop() / CALENDAR_PIXELS_PER_MINUTE;

    return {
      start,
      end: start + this.viewportHeight() / CALENDAR_PIXELS_PER_MINUTE,
    };
  });

  /** Hints to scroll to times on the shown days that are out of view */
  readonly scrollHints = computed(() => {
    const { earlier, later } = getHiddenMinutes(
      this.items(),
      this.visibleMinuteRange()
    );

    return {
      earlier: Number.isFinite(earlier) && {
        label: `Earlier times from ${formatMinutes(earlier)}`,
        top: minutesToPixels(earlier) - SCROLL_HINT_MARGIN,
      },
      later: Number.isFinite(later) && {
        label: `Later times until ${formatMinutes(later)}`,
        top:
          minutesToPixels(later) - this.viewportHeight() + SCROLL_HINT_MARGIN,
      },
    };
  });

  readonly days = computed<ICalendarDay[]>(() => {
    const today = this.zonedNow().format(DATE_INPUT_FORMAT);

    return this.dates().map((date) => ({
      date,
      isToday: date === today,
      isInPast: date < today,
    }));
  });

  readonly height = CALENDAR_HEIGHT;

  /** Labels for the hours between the top and bottom of the grid */
  readonly hourLabels = CALENDAR_HOURS.slice(1, -1).map((minutes) => ({
    top: minutesToPixels(minutes),
    label: formatMinutes(minutes),
  }));

  readonly nowLabel = computed(() => {
    const minutes = this.nowMinutes();

    return this.days().some((day) => day.isToday)
      ? { top: minutesToPixels(minutes), label: formatMinutes(minutes) }
      : null;
  });

  constructor() {
    afterNextRender({
      write: () => {
        const scroller = this.scroller().nativeElement;

        scroller.scrollTop = this.scrollTop();

        const resizeObserver = new ResizeObserver(() =>
          this.viewportHeight.set(scroller.clientHeight - DAY_HEADER_HEIGHT)
        );
        resizeObserver.observe(scroller);
        this.destroyRef.onDestroy(() => resizeObserver.disconnect());
      },
    });
  }

  onScroll() {
    this.scrollTop.set(this.scroller().nativeElement.scrollTop);
  }

  scrollTo(top: number) {
    this.scroller().nativeElement.scrollTo({ top, behavior: 'smooth' });
  }
}
